import {actions, ActionType} from './action';
import {INotice, ITorosInfo} from '../../common/type';
import {
  createReducer,
  ReducerActionHelper,
  createBasicReducerHandlers,
} from '../../common/util/redux';
import {IRecommendItemContent} from '../../commonSub/type';
import {
  IContent,
  IContentProfile,
  IEpisode,
  IPurchase,
  IContentTicket,
  IKeywordContent,
  PromotionVideoStatus,
  IContentAdBanner,
  IContentTicketInfo,
  IContentAddedTicketItem,
  IContentUsedTicketItem,
  IContentTicketDetail,
  IViewData,
  IContentTabV4,
} from '../type';

export interface IStateContent {
  profile?: IContentProfile;
  // 한번 방문한 작품의 에피소드 정보를 map 형태로 저장한다.
  // TODO: 갱신 시점을 생각해보자. 데이터 구조는 변경될 수 있다.
  contentMap: {[id: number]: IContent};
  episodeMap: {[id: number]: IEpisode | undefined};
  promotionVideoStatus: PromotionVideoStatus;
  isPlayingMotionVideo: boolean;
  currentId?: number;
  purchase?: IPurchase;
  purchaseVoucher: {
    count: number;
    price: number;
    totalCount: number;
  };
  contentTicket: IContentTicket;
  giftTicketCount: number;
  contentAdBanner?: IContentAdBanner;
  keywordContents: IKeywordContent[];
  selectedEpisode?: {
    episodeId: number;
    episodeSid: string;
    isTryReadAgain: boolean;
  };
  contentKey: number;
  hasRedDot: boolean;
  isClickedMetaText: boolean;
  isSoundOn: boolean;
  contentTicketHistoryInfo?: IContentTicketInfo;
  contentTicketHistory: {
    added: IContentAddedTicketItem[];
    used: IContentUsedTicketItem[];
  };
  contentTicketDetail?: IContentTicketDetail;
  // V4
  mainHeightV4: number;
  isFirstRead?: boolean;
  viewData?: IViewData;
  recommendContents: {items: IRecommendItemContent[]; torosInfo?: ITorosInfo};
  isImageReady: boolean;
  tabList: Array<{key: IContentTabV4; text: string}>;
  recentNotice?: INotice;
}

const INITIAL_CONTENT_TICKET: IContentTicket = {
  ticketCount: 0,
  welcomeTicketCount: 0,
  freeEpisodeCount: 0,
  allFree: false,
  isFreePublishing: false,
  isWaitfreePlus: false,
};

const INITIAL_STATE: IStateContent = {
  promotionVideoStatus: 'hidden',
  isPlayingMotionVideo: false,
  contentMap: {},
  episodeMap: {},
  purchase: undefined,
  purchaseVoucher: {
    count: 1,
    price: 0,
    totalCount: 1,
  },
  contentTicket: INITIAL_CONTENT_TICKET,
  giftTicketCount: 0,
  keywordContents: [],
  contentKey: 0,
  hasRedDot: false,
  isClickedMetaText: true,
  isSoundOn: false,
  contentTicketHistory: {
    added: [],
    used: [],
  },
  // V4
  mainHeightV4: 0,
  recommendContents: {items: []},
  isImageReady: false,
  tabList: [],
};

export default createReducer<
  IStateContent,
  ReducerActionHelper<typeof actions>
>(INITIAL_STATE, {
  ...createBasicReducerHandlers({
    setValueActionType: ActionType.SetValue,
    setPropertyValuesActionType: ActionType.SetPropertyValues,
    removeValuesActionType: ActionType.RemoveValues,
  }),
  [ActionType.SetContent]: (state, action) => {
    const {content} = action.payload;
    if (!state.contentMap[content.id]) {
      state.contentMap[content.id] = content;
    }
    state.currentId = content.id;
  },
  [ActionType.SetContentAdditional]: (state, action) => {
    const {contentTicket, giftTicketCount, contentAdBanner} = action.payload;
    contentTicket && (state.contentTicket = contentTicket);
    state.giftTicketCount = giftTicketCount || 0;
    state.contentAdBanner = contentAdBanner;
  },
  [ActionType.AddEpisode]: (state, action) => {
    const {contentId, isFirst, episode} = action.payload;
    if (isFirst) {
      state.episodeMap[contentId] = episode;
    } else {
      const storedEpisode = state.episodeMap[contentId];
      storedEpisode && storedEpisode.items.push(...episode.items);
    }
  },
  [ActionType.RemoveEpisode]: (state, action) => {
    const {contentId} = action.payload;
    delete state.episodeMap[contentId];
  },
  [ActionType.SetEpisodeRead]: (state, action) => {
    const {contentId, episodeId, type, timeText, badge} = action.payload;
    const episode = state.episodeMap[contentId];
    const targetEpisode = episode?.items.find(item => item.id === episodeId);
    if (episode && targetEpisode) {
      /**
       * 티켓 사용 후 API에서 내려준 episode의 isRead는 false다.
       * 뷰어에서 이미지 리소스를 받은 후 isRead가 변경되지만,
       * UI 업데이트를 위한 값이므로 프론트에서 isRead를 변경해준다.
       */
      targetEpisode.isRead = true;
      targetEpisode.timeText = timeText;
      targetEpisode.type = type;
      targetEpisode.badge = badge;
      const {id, sid, title} = targetEpisode;
      episode.lastRead = {id, sid, title, type};
      episode.floatingButtonType = 'continueEpisode';
    }
  },
  [ActionType.ReceiveGiftTicket]: (state, action) => {
    const {giftTicketCount, isCancelled} = action.payload;
    if (isCancelled) {
      state.giftTicketCount = giftTicketCount;
      state.contentTicket.ticketCount =
        state.contentTicket.ticketCount - giftTicketCount;
    } else {
      state.giftTicketCount = 0;
      state.contentTicket.ticketCount =
        state.contentTicket.ticketCount + giftTicketCount;
    }
  },
  [ActionType.AddKeywordContents]: (state, action) => {
    const {contents, isFirst} = action.payload;
    if (isFirst) {
      state.keywordContents = contents;
    } else {
      state.keywordContents.push(...contents);
    }
  },
  [ActionType.InitializeAdditional]: state => {
    state.contentTicket = INITIAL_CONTENT_TICKET;
    state.giftTicketCount = 0;
    state.contentAdBanner = undefined;
  },
  [ActionType.InitializeTab]: state => {
    state.mainHeightV4 = 0;
    state.isFirstRead = true;
    state.viewData = undefined;
    state.profile = undefined;
    state.recentNotice = undefined;
  },
  [ActionType.AddContentTicketContents]: (state, action) => {
    const {tab, ticketInfo, contents, isFirst} = action.payload;
    ticketInfo && (state.contentTicketHistoryInfo = ticketInfo);

    if (isFirst) {
      // @ts-ignore: 액션 생성자에게 타입을 제대로 정의해서 괜찮다
      state.contentTicketHistory[tab] = contents;
    } else {
      // @ts-ignore: 액션 생성자에게 타입을 제대로 정의해서 괜찮다
      state.contentTicketHistory[tab].push(...contents);
    }
  },
  [ActionType.SetImageUrl]: (state, action) => {
    const {index, url, episodeId} = action.payload;
    if (
      state.viewData?.episode?.id === episodeId &&
      state.viewData.images.length > index
    ) {
      state.viewData.images[index].url = url;
      state.isImageReady = true;
    }
  },
});
